import {
  NOT_CRAWLER_SCOPED,
  type CrawlerCategory,
  type CrawlerEvidenceRowResponse,
  type IncidentDetailResponse,
  type IncidentDomainResponse,
} from '@prerender/diagnostics-api';

import { affectedDomainsLabel, formatRequestCount, spansSeveralDomains } from './crawlerEvidenceFigures';
import { crawlerLabel } from './crawlerLabels';
import { formatDetectedAt } from './formatDetectedAt';
import { formatRate } from './formatMeasuredValue';
import { notFoundRenderFiguresOf } from './notFoundRenderFigures';
import { blockingFiguresOf } from './originBlockingFigures';

export type IncidentNarrative = {
  whatHappened: string;
  likelyRootCause: string;
  suggestedNextStep: string;
};

export type NarrativeSubject = Pick<
  IncidentDetailResponse,
  | 'incidentType'
  | 'status'
  | 'metricValue'
  | 'firstDetectedAt'
  | 'lastUpdatedAt'
  | 'lastDetectedAt'
  | 'crawlerCategory'
  | 'crawlerEvidence'
  | 'affectedDomainCount'
  | 'headlineDomain'
> &
  Pick<IncidentDomainResponse, 'domain' | 'evidence'>;

export function narrativeSubjectOf(incident: IncidentDetailResponse): NarrativeSubject | null {
  const told = incident.domains.find((row) => row.domain === incident.headlineDomain) ?? incident.domains[0];
  if (told === undefined) return null;

  return { ...incident, domain: told.domain, evidence: told.evidence };
}

const shown = (instant: string): string => formatDetectedAt(new Date(instant));

const causes = (...possibilities: readonly string[]): string => {
  const listed = possibilities
    .map((possibility, index) => (index === possibilities.length - 1 ? `or ${possibility}` : possibility))
    .join('; ');

  return `One of these, none confirmed by this check: ${listed}.`;
};

function notFoundRenderNarrative({
  domain,
  metricValue,
  evidence,
  lastUpdatedAt,
}: NarrativeSubject): IncidentNarrative | null {
  // The detector versions its evidence, so a narrative reads the shape it was written for
  // and stays silent on any other — a wrong explanation costs more than a missing one.
  const notFound = notFoundRenderFiguresOf(evidence);
  if (notFound === null) return null;

  const { onDemandRequests, requests404 } = notFound;

  return {
    whatHappened: `${metricValue}% of the crawler requests Prerender answered for ${domain} in the seven days ending ${shown(lastUpdatedAt)} returned 404: ${requests404} of ${onDemandRequests}.`,
    likelyRootCause: causes(
      'pages that were removed are still linked or still listed in a sitemap',
      'crawlers are following URL patterns the site never served, such as query strings or old paths',
      'the origin answers 404 for pages that do exist, which would be a routing or deployment fault',
      'something in front of the origin answers 404 to Prerender while serving the page normally to everyone else'
    ),
    suggestedNextStep: `Contact the customer. This check sees the domain's status codes, not individual URLs, so ask them to pull the 404s for ${domain} from their own origin logs. If those paths are meant to be gone, taking them out of the sitemap and internal links stops crawlers asking. If the pages are supposed to work, the 404 is coming from their origin and belongs with their engineering team.`,
  };
}

function originBlockingNarrative({
  domain,
  metricValue,
  evidence,
  lastDetectedAt,
}: NarrativeSubject): IncidentNarrative | null {
  const blocking = blockingFiguresOf(evidence);
  if (blocking === null) return null;

  const { totalRequests, blockedRequests } = blocking;

  return {
    whatHappened: `${formatRate(metricValue)} of the crawler requests Prerender answered for ${domain} in the seven days ending ${shown(lastDetectedAt)} came back with an error status: ${blockedRequests} of ${totalRequests} were answered 403, 503 or 504. Crawlers asking for those pages got an error status instead of the page.`,
    likelyRootCause: causes(
      "a firewall, WAF or CDN rule is refusing Prerender's requests, which is what a 403 usually means",
      'HTTP authentication covers the paths being crawled',
      'geo-blocking excludes the region Prerender renders from',
      'the origin was too slow or overloaded and answered 503 or 504 itself, which is capacity rather than blocking',
      'the error never reached the origin at all — Prerender answers 504 when a render fails, times out or comes back empty, and when an account is set to serve cache hits only'
    ),
    suggestedNextStep: `Contact the customer about the 403s: ask them to allowlist Prerender's published IP ranges wherever a firewall, CDN or WAF sits in front of their origin, and to confirm no authentication or geo rule covers ${domain}. If nothing in front of the origin is refusing us, check this account's own settings and render errors before escalating — a 504 can be ours.`,
  };
}

type CategoryReading = {
  causes: readonly string[];
  note: string | null;
  check: string | null;
};

const INTEGRATION_CHANGE_CAUSE = "a change to the site's integration stopped Prerender receiving crawler requests";
const ACCOUNT_STOPPED_CAUSE =
  'the account stopped sending its traffic to Prerender at all, through a cancelled subscription or a removed domain';

const FIREWALL_CAUSE = 'a firewall, WAF or CDN rule began refusing crawler requests before they reached Prerender';

const NEUTRAL_CAUSES = [
  INTEGRATION_CHANGE_CAUSE,
  FIREWALL_CAUSE,
  ACCOUNT_STOPPED_CAUSE,
  'less crawling is being asked for in the first place, through a smaller crawl budget, a finished migration or a slowdown of the crawling itself',
] as const;

const NEUTRAL_READING: CategoryReading = { causes: NEUTRAL_CAUSES, note: null, check: null };

const SEARCH_READING: CategoryReading = {
  causes: [
    INTEGRATION_CHANGE_CAUSE,
    FIREWALL_CAUSE,
    ACCOUNT_STOPPED_CAUSE,
    "the search engines are asking for fewer of the customer's pages, through a smaller crawl budget",
  ],
  note: null,
  check: null,
};

const META_SOCIAL_READING: CategoryReading = {
  causes: NEUTRAL_CAUSES,
  note: 'Meta mixes its link-preview and AI crawlers under one name, so this drop does not tell which of the two kinds of traffic fell.',
  check: null,
};

const SOCIAL_LINK_SHARE_READING: CategoryReading = {
  causes: [
    ...NEUTRAL_CAUSES,
    "fewer links to the customer's pages may be shared on social media, since social media crawlers fetch a page when someone shares its link",
  ],
  note: null,
  check: null,
};

const AI_READING: CategoryReading = {
  causes: [
    'the customer blocks AI crawlers on purpose, through robots.txt, a CDN AI-bot setting or a firewall rule',
    INTEGRATION_CHANGE_CAUSE,
    ACCOUNT_STOPPED_CAUSE,
  ],
  note: null,
  check:
    'Ask also whether they block AI crawlers on purpose, in robots.txt, a CDN AI-bot setting or a firewall rule: a deliberate block is their choice, not a fault to fix.',
};

const CATEGORY_READINGS: Record<CrawlerCategory, (stillDownCrawlers: readonly string[]) => CategoryReading> = {
  search: () => SEARCH_READING,
  ai: () => AI_READING,
  social: (stillDownCrawlers) => (stillDownCrawlers.includes('meta') ? META_SOCIAL_READING : SOCIAL_LINK_SHARE_READING),
  other: () => NEUTRAL_READING,
};

const listed = (names: readonly string[]): string =>
  names.length === 1 ? names[0] : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;

const stillDownCrawlersOf = (evidence: NarrativeSubject['crawlerEvidence']): CrawlerEvidenceRowResponse[] =>
  evidence.flatMap((provider) => provider.crawlers).filter((row) => !row.noLongerAffected);

const crawlerDomains = (row: CrawlerEvidenceRowResponse): string =>
  spansSeveralDomains(row) ? affectedDomainsLabel(row.affectedDomainCount) : row.worstDomain;

const worstDomainClause = (row: CrawlerEvidenceRowResponse): string =>
  spansSeveralDomains(row) ? ` (up to ${row.worstMetricValue}% on ${row.worstDomain})` : '';

const crawlerFigures =
  (incidentDomainCount: number) =>
  (row: CrawlerEvidenceRowResponse): string => {
    const where = incidentDomainCount === 1 ? '' : ` on ${crawlerDomains(row)}`;
    return `${crawlerLabel(row.crawler)} sent ${formatRequestCount(row.currentRequests)} requests${where}, ${row.metricValue}% below a usual week of about ${formatRequestCount(row.usualWeekRequests)}${worstDomainClause(row)}`;
  };

const withSentence = (text: string, sentence: string | null): string => (sentence === null ? text : `${text} ${sentence}`);

function crawlerDropNarrative({
  crawlerCategory,
  crawlerEvidence,
  affectedDomainCount,
  headlineDomain,
  firstDetectedAt,
  lastUpdatedAt,
}: NarrativeSubject): IncidentNarrative | null {
  const stillDown = stillDownCrawlersOf(crawlerEvidence);
  if (crawlerCategory === NOT_CRAWLER_SCOPED || stillDown.length === 0) return null;

  const reading = CATEGORY_READINGS[crawlerCategory](stillDown.map((row) => row.crawler));
  const named = listed(stillDown.map((row) => crawlerLabel(row.crawler)));
  const where = affectedDomainCount === 1 ? headlineDomain : `${affectedDomainCount} domains`;

  return {
    whatHappened: `Prerender received fewer requests than usual from ${named} on ${where} in the seven days ending ${shown(lastUpdatedAt)}: ${stillDown.map(crawlerFigures(affectedDomainCount)).join('; ')}. A usual week is the average of the four weeks before the week in which the drop was first seen, on ${shown(firstDetectedAt)}; while this incident is open, each check measures the latest seven days against that same level.`,
    likelyRootCause: withSentence(causes(...reading.causes), reading.note),
    suggestedNextStep: withSentence(
      `Contact the customer. Ask whether anything changed in their integration, their firewall or their CDN in the week before ${shown(firstDetectedAt)}, when the drop was first seen. A request Prerender answered with an error still counts here, so a plan limit or a block on our own side raises a different incident, not this one. Before treating it as one customer's fault, check whether other customers show the same drop for ${named} — a slowdown that hits every site on the web looks the same here.`,
      reading.check
    ),
  };
}

const NARRATIVES: Record<string, (subject: NarrativeSubject) => IncidentNarrative | null> = {
  po1: notFoundRenderNarrative,
  re1: originBlockingNarrative,
  sd1: crawlerDropNarrative,
};

// Every narrative is written in the present tense, so none of them may be told once the
// incident is over — a recovered or closed one is carried by the status rail and the history.
// A text written in the past tense would need this gate moved, not deleted.
export function narrativeFor(subject: NarrativeSubject): IncidentNarrative | null {
  if (subject.status !== 'active') return null;

  const narrative = NARRATIVES[subject.incidentType];

  return narrative ? narrative(subject) : null;
}
